import type { TFunction } from 'i18next';
import type { ApprovalNoticeType } from './ApprovalNoticeBanner';

export interface CongViecApprovalNotice {
  type: ApprovalNoticeType;
  message: string;
}

export interface ResolveCongViecApprovalNoticesParams {
  stateApproved: number;
  nguoiPhuTrachID?: string | null;
  /** Người tạo công việc — banner reconfirm chỉ hiện cho user này. */
  nguoiTaoID?: string | null;
  boPhanPhuTrachID?: string | null;
  nguoiHoTroID?: string | null;
  nguoiHoTroApproved?: string | null;
  userID: string;
  userBoPhanID?: string | null;
  isQuanLy?: boolean;
  /** Bật rule bộ phận QL (modal công việc V1/V2). Modal dự án có thể tắt. */
  includeBoPhanRule?: boolean;
  t: TFunction;
}

/**
 * Rule ver cũ (ChiTiet.cshtml) + yêu cầu mới:
 * - state=0 + là phụ trách → Đồng ý/Từ chối
 * - state=0 + QL bộ phận → Đồng ý/Từ chối bộ phận
 * - state=0 khác → Chờ xác nhận
 * - state=2 → banner từ chối; banner "Đồng ý làm phụ trách chính" chỉ cho người tạo
 * - hỗ trợ chờ duyệt → Đồng ý/Từ chối hỗ trợ
 */
export function resolveCongViecApprovalNotices(
  params: ResolveCongViecApprovalNoticesParams
): CongViecApprovalNotice[] {
  const {
    stateApproved,
    nguoiPhuTrachID,
    nguoiTaoID,
    boPhanPhuTrachID,
    nguoiHoTroID = '',
    nguoiHoTroApproved = '',
    userID,
    userBoPhanID = '',
    isQuanLy = false,
    includeBoPhanRule = true,
    t,
  } = params;

  if (!userID) return [];

  // Hỗ trợ cả 1 ID lẫn chuỗi "id1,id2" (trước khi BE tách thành nhiều công việc).
  const phuTrachIds = String(nguoiPhuTrachID || '')
    .split(/[,;]/)
    .map((s) => s.trim())
    .filter(Boolean);
  const isPhuTrach = phuTrachIds.includes(userID);
  const isNguoiTao = Boolean(nguoiTaoID && String(nguoiTaoID) === userID);
  const isBoPhanQl =
    includeBoPhanRule &&
    Boolean(
      boPhanPhuTrachID &&
        String(boPhanPhuTrachID) === String(userBoPhanID) &&
        isQuanLy
    );
  const listHoTro = `,${String(nguoiHoTroID).replace(/^,|,$/g, '')},`;
  const listApproved = `,${String(nguoiHoTroApproved).replace(/^,|,$/g, '')},`;
  const userInHoTroPending =
    listHoTro.includes(`,${userID},`) && listApproved.includes(`,${userID},`);

  const rejectedMsg = t(
    'congviec.dangchoxacnhan.tuchoi',
    'Bộ Phận/Người Phụ Trách đã từ chối tiếp nhận công việc này'
  );
  const reconfirmMsg = t(
    'bsc.msg.bandongytuchoi.reconfirm',
    'Đồng ý làm người phụ trách chính cho công việc này.'
  );

  if (stateApproved === 2) {
    const notices: CongViecApprovalNotice[] = [{ type: 'rejected', message: rejectedMsg }];
    // Chỉ người tạo được nhận lại phụ trách sau khi bị từ chối.
    if (isNguoiTao) {
      notices.push({ type: 'reconfirm', message: reconfirmMsg });
    }
    return notices;
  }

  if (isPhuTrach && stateApproved === 0) {
    return [
      {
        type: 'phuTrach',
        message: t(
          'bsc.msg.bandongytuchoi',
          'Bạn được chọn làm người phụ trách chính cho công việc này, vui lòng xác nhận.'
        ),
      },
    ];
  }

  if (stateApproved === 0 && isBoPhanQl) {
    return [
      {
        type: 'boPhan',
        message: t(
          'bsc.msg.bophandongy',
          'Bộ phận của bạn được chọn làm bộ phận phụ trách chính cho công việc này, vui lòng xác nhận.'
        ),
      },
    ];
  }

  if (userInHoTroPending) {
    return [
      {
        type: 'hoTro',
        message: t(
          'bsc.msg.banhotrodongytuchoi',
          'Bạn được thêm vào làm người hỗ trợ cho công việc này, vui lòng xác nhận.'
        ),
      },
    ];
  }

  if (stateApproved === 0) {
    return [{ type: 'choXacNhan', message: t('bsc.label.choxacnhan', 'Chờ xác nhận') }];
  }

  return [];
}
